import { useCallback, useEffect, useState } from "react";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { listMetasProspeccao, upsertMetaProspeccao } from "@/lib/api";
import { fmtInt, fmtPct, pct } from "@/lib/format";
import { useAuth, isAdmin } from "@/lib/auth";

export default function MetasProspeccao({ prospectosPorVendedor = {}, vendedores = [] }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [metas, setMetas] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await listMetasProspeccao();
      const map = {};
      for (const m of list) map[m.vendedor] = Number(m.meta_mensal || 0);
      setMetas(map);
    } catch (err) {
      console.error("Erro ao carregar metas de prospecção:", err);
      toast.error("Não foi possível carregar as metas de prospecção");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (vendedor) => {
    const value = Math.max(0, parseInt(drafts[vendedor] ?? metas[vendedor] ?? 0, 10) || 0);
    setSaving(vendedor);
    try {
      await upsertMetaProspeccao(vendedor, value);
      setMetas((p) => ({ ...p, [vendedor]: value }));
      setDrafts((p) => { const n = { ...p }; delete n[vendedor]; return n; });
      toast.success(`Meta de prospecção de ${vendedor} salva`);
    } catch { toast.error("Erro ao salvar meta"); }
    finally { setSaving(null); }
  };

  const lista = vendedores.length ? vendedores : Object.keys(prospectosPorVendedor);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="metas-prospeccao">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-semibold text-lg">Meta de prospecção mensal</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Quantidade de prospectos (leads futuros captados) que cada vendedor deve registrar por mês.
      </p>

      {lista.length === 0 && <div className="text-sm text-muted-foreground">Nenhum vendedor com prospectos ainda.</div>}

      <div className="space-y-3">
        {lista.map((v) => {
          const meta = drafts[v] !== undefined ? drafts[v] : (metas[v] ?? 0);
          const metaNum = parseInt(meta, 10) || 0;
          const atual = Number(prospectosPorVendedor[v] || 0);
          const p = pct(atual, metaNum);
          const tone = p >= 100 ? "text-emerald-500" : p >= 50 ? "text-amber-500" : "text-red-500";
          return (
            <div key={v} className="rounded-lg border border-border p-4" data-testid={`meta-prosp-${v}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{v}</div>
                  <div className="text-xs text-muted-foreground">
                    Prospectos no mês: <span className="font-mono text-foreground">{fmtInt(atual)}</span>
                    {metaNum > 0 ? ` / ${fmtInt(metaNum)}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-heading text-2xl font-extrabold tabular-nums ${tone}`}>{metaNum > 0 ? fmtPct(p) : "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">atingido</div>
                </div>
              </div>
              <Progress value={Math.min(p, 100)} className="h-2 mb-3" />
              {admin && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs mb-1 block">Meta mensal (nº de prospectos)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={meta}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [v]: e.target.value }))}
                      data-testid={`input-meta-prosp-${v}`}
                    />
                  </div>
                  <Button onClick={() => handleSave(v)} disabled={saving === v} data-testid={`btn-save-meta-prosp-${v}`}>
                    <Save className="w-4 h-4 mr-2" /> Salvar
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
